import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { saveOrgUser } from "../../utils/orgUsers";
import Card from "../../ui/Card";
import Table from "../../ui/Table";
import { auth } from "../../firebase";

const VALID_ROLES = new Set(["member", "coach", "admin", "superadmin", "investor"]);
const PENDING_INVITE_ROLE = "member";
const PLACEHOLDER_PROJECT_RE = /your_project/i;

function resolveFunctionsBaseUrl(projectId) {
  const envBase = String(process.env.REACT_APP_FUNCTIONS_BASE_URL || "").trim();
  if (envBase && !PLACEHOLDER_PROJECT_RE.test(envBase)) {
    if (projectId && envBase.includes(`africa-south1-${projectId}.cloudfunctions.net`)) {
      return envBase
        .replace(`africa-south1-${projectId}.cloudfunctions.net`, `europe-west1-${projectId}.cloudfunctions.net`)
        .replace(/\/+$/, "");
    }
    return envBase.replace(/\/+$/, "");
  }
  return projectId ? `https://europe-west1-${projectId}.cloudfunctions.net` : "";
}

export function parseCsv(str) {
  // very small parser: "name,email,role" per line
  const lines = (str || "").trim().split(/\r?\n/).filter(Boolean);
  const rows = lines.map((line) => {
    const [name = "", email = "", role = "member"] = line.split(",").map((s) => s.trim());
    return { Name: name, Email: email, Role: role || "member" };
  });
  return rows;
}

export function validateRows(rows) {
  const errors = [];
  const seen = new Set();
  rows.forEach((row, idx) => {
    const email = row.Email?.toLowerCase() || "";
    if (!row.Name) errors.push(`Row ${idx + 1}: name is required.`);
    if (!email || !email.includes("@")) errors.push(`Row ${idx + 1}: valid email required.`);
    if (seen.has(email)) errors.push(`Row ${idx + 1}: duplicate email ${email}.`);
    seen.add(email);
    const role = String(row.Role || PENDING_INVITE_ROLE).trim().toLowerCase();
    if (!VALID_ROLES.has(role)) {
      errors.push(`Row ${idx + 1}: invalid role "${row.Role}".`);
    } else if (role !== PENDING_INVITE_ROLE) {
      errors.push(
        `Row ${idx + 1}: pending CSV invitations support member only; use Create User for privileged roles.`
      );
    }
  });
  return errors;
}

export function canSavePendingInvites({
  isReadOnly = false,
  rows = [],
  errors = [],
} = {}) {
  return !isReadOnly && rows.length > 0 && errors.length === 0;
}

export function inviteImportStatus(outcomes = []) {
  const rows = Array.isArray(outcomes) ? outcomes : [];
  const saved = rows.filter((outcome) => outcome?.status === "Saved").length;
  const failed = rows.length - saved;
  if (!rows.length) return "";
  if (!failed) {
    return "Invites saved. Ask users to sign up only if their account was not already created by admin.";
  }
  if (!saved) {
    return `No invites were saved (${failed} failed). Review the row results and try again.`;
  }
  return `${saved} invite(s) saved; ${failed} failed. Review the row results before sharing signup instructions.`;
}

export default function InviteUsersPage() {
  const { user, activeOrgId } = useAuth();
  const isReadOnly = user?.role === "investor";
  const [status, setStatus] = useState("");
  const [results, setResults] = useState([]);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createRole, setCreateRole] = useState("member");
  const [createStatus, setCreateStatus] = useState("");
  const [createResult, setCreateResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const [reminderStatus, setReminderStatus] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  const demoLink = useMemo(() => {
    const orgId = activeOrgId || user?.orgId || "org_default";
    return `${window.location.origin}/signup?org=${orgId}`;
  }, [activeOrgId, user?.orgId]);
  const [csv, setCsv] = useState("");
  const rows = parseCsv(csv);
  const rowErrors = validateRows(rows);
  const orgId = activeOrgId || user?.orgId || "org_default";

  const createOrgUserNow = async () => {
    const email = createEmail.trim().toLowerCase();
    const username = createName.trim();
    const role = String(createRole || "member").toLowerCase();
    if (!email || !email.includes("@")) {
      setCreateStatus("Enter a valid email.");
      return;
    }
    if (!username) {
      setCreateStatus("Name is required.");
      return;
    }
    if (!VALID_ROLES.has(role)) {
      setCreateStatus("Invalid role selected.");
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setCreateStatus("You must be signed in to create users.");
      return;
    }
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
    const base = resolveFunctionsBaseUrl(projectId);
    if (!base) {
      setCreateStatus("Missing Functions URL config. Keep using invite link/CSV flow.");
      return;
    }

    setCreating(true);
    setCreateStatus("Creating user...");
    setCreateResult(null);
    try {
      const token = await currentUser.getIdToken();
      const endpoint = `${base}/createOrgUser`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          role,
          orgId,
          username,
          loginUrl: `${window.location.origin}/login`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Unable to create user.");
      }
      setCreateResult({
        uid: payload?.uid || "",
        tempPassword: payload?.tempPassword || "",
        email,
        outcome: String(payload?.outcome || "created"),
        message: String(payload?.message || ""),
        onboardingEmailSent: Boolean(payload?.onboardingEmailSent),
        onboardingEmailError: String(payload?.onboardingEmailError || ""),
        linkNotificationSent: typeof payload?.linkNotificationSent === "boolean" ? payload.linkNotificationSent : null,
        linkNotificationError: String(payload?.linkNotificationError || ""),
      });
      setCreateStatus(String(payload?.message || "User created."));
      setCreateName("");
      setCreateEmail("");
      setCreateRole("member");
      setReminderStatus("");
    } catch (err) {
      setCreateStatus(String(err?.message || "User creation failed."));
    } finally {
      setCreating(false);
    }
  };

  const sendReminder = async () => {
    const email = String(createResult?.email || "").trim().toLowerCase();
    if (!email) {
      setReminderStatus("No created user email found.");
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setReminderStatus("You must be signed in to send reminders.");
      return;
    }
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
    const base = resolveFunctionsBaseUrl(projectId);
    if (!base) {
      setReminderStatus("Missing Functions URL config.");
      return;
    }
    setSendingReminder(true);
    setReminderStatus("Sending reminder...");
    try {
      const token = await currentUser.getIdToken();
      const endpoint = `${base}/sendOrgUserReminder`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          orgId,
          loginUrl: `${window.location.origin}/login`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Unable to send reminder.");
      }
      setReminderStatus("Reminder sent.");
    } catch (err) {
      setReminderStatus(String(err?.message || "Reminder failed."));
    } finally {
      setSendingReminder(false);
    }
  };

  const inviteFromCsv = async () => {
    if (!rows.length) return;
    if (rowErrors.length) {
      setStatus("");
      return;
    }
    if (!orgId) {
      setStatus("Select an active organisation before importing CSV.");
      return;
    }
    setStatus("Saving invite list...");
    const outcomes = [];
    for (const row of rows) {
      try {
        await saveOrgUser(user, orgId, {
          id: `${row.Email}_${Date.now()}`,
          orgId,
          username: row.Name,
          email: row.Email,
          role: PENDING_INVITE_ROLE,
          status: "pending",
          invitedAt: Date.now(),
        });
        outcomes.push({
          name: row.Name,
          email: row.Email,
          status: "Saved",
        });
      } catch {
        outcomes.push({ name: row.Name, email: row.Email, status: "Error" });
      }
    }
    setResults(outcomes);
    setStatus(inviteImportStatus(outcomes));
    setTimeout(() => setStatus(""), 4000);
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <h3 className="text-lg font-semibold">Invite Instructions</h3>
        <div className="mt-2 text-sm opacity-80">
          1. Preferred pilot flow: create users directly below (admin/superadmin). <br />
          2. Created users should log in (not sign up) and use reset password if needed. <br />
          3. Fallback: use CSV or invite link only when no account has been created yet.
        </div>
        <div className="mt-3 text-sm opacity-70">
          Direct creation uses the existing secure Cloud Function. Invite flow remains available as fallback.
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold">Create user now (pilot)</h3>
        <p className="opacity-80 mt-1">Creates Firebase Auth user + Firestore user/org records in one step.</p>
        <div className="mt-3 grid gap-2">
          <input
            className="input"
            placeholder="Full name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={isReadOnly || creating}
          />
          <input
            className="input"
            type="email"
            placeholder="Email"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            disabled={isReadOnly || creating}
          />
          <select
            className="input"
            value={createRole}
            onChange={(e) => setCreateRole(e.target.value)}
            disabled={isReadOnly || creating}
          >
            <option value="member">member</option>
            <option value="coach">coach</option>
            <option value="admin">admin</option>
            <option value="superadmin">superadmin</option>
            <option value="investor">investor</option>
          </select>
          <button
            className="btn btn-primary"
            onClick={createOrgUserNow}
            disabled={isReadOnly || creating}
          >
            {creating ? "Creating..." : "Create User"}
          </button>
          {createStatus && <div className="text-sm opacity-70">{createStatus}</div>}
          {createResult && (
            <div className="text-sm">
              <div><strong>Outcome:</strong> {createResult.outcome || "created"}</div>
              {createResult.message && <div><strong>Detail:</strong> {createResult.message}</div>}
              <div><strong>UID:</strong> {createResult.uid || "-"}</div>
              <div><strong>Temporary password:</strong> {createResult.tempPassword || (createResult.outcome === "created" ? "-" : "N/A (existing account)")}</div>
              <div>
                <strong>{createResult.outcome === "created" ? "Welcome email:" : "Linked-access email:"}</strong>{" "}
                {createResult.outcome === "created"
                  ? (createResult.onboardingEmailSent ? "Sent" : "Not sent (check email setup)")
                  : (createResult.linkNotificationSent ? "Sent" : "Not sent")}
              </div>
              {!createResult.onboardingEmailSent && createResult.onboardingEmailError && createResult.outcome === "created" && (
                <div><strong>Email detail:</strong> {createResult.onboardingEmailError}</div>
              )}
              {createResult.outcome !== "created" && createResult.linkNotificationError && (
                <div><strong>Email detail:</strong> {createResult.linkNotificationError}</div>
              )}
              <div style={{ marginTop: 6 }}>
                {createResult.outcome === "created"
                  ? "Account is now created. Send user to Login, not Signup. If they cannot access, use Forgot password on login."
                  : "Existing account linked. User should continue with Login in the selected organisation."}
              </div>
              {createResult.outcome === "created" && (
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-ghost" onClick={sendReminder} disabled={sendingReminder || creating}>
                    {sendingReminder ? "Sending reminder..." : "Send reminder"}
                  </button>
                </div>
              )}
              {createResult.outcome === "created" && reminderStatus && (
                <div className="text-sm opacity-70" style={{ marginTop: 6 }}>{reminderStatus}</div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold">Share invite link</h3>
        <p className="opacity-80 mt-1">
          Use this only for users who do not already have an admin-created account.
        </p>
        <div className="mt-3 flex gap-2">
          <input className="input grow" readOnly value={demoLink} />
          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(demoLink);
                setStatus("Invite link copied.");
                setTimeout(() => setStatus(""), 3000);
              } catch {
                setStatus("Unable to copy invite link.");
                setTimeout(() => setStatus(""), 3000);
              }
            }}
            disabled={isReadOnly}
          >
            Copy
          </button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold">Bulk member invite via CSV</h3>
        <p className="opacity-80 mt-1">
          Paste lines as <code>name,email,member</code> (role optional). Pending CSV invitations
          support members only; use Create User above for privileged roles. If admin already
          created an account, the user should log in instead of signing up.
        </p>
        <textarea
          rows={6}
          className="textarea mt-3"
          placeholder={`Jane Doe,jane@acme.com,member\nSam Member,sam@acme.com`}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          disabled={isReadOnly}
        />
        <button
          className="btn btn-primary"
          style={{ marginTop: 12 }}
          onClick={inviteFromCsv}
          disabled={!canSavePendingInvites({
            isReadOnly,
            rows,
            errors: rowErrors,
          })}
        >
          Save Invites
        </button>
        {rowErrors.length > 0 && (
          <div className="mt-3 text-sm" style={{ color: "var(--color-danger)" }}>
            {rowErrors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </div>
        )}
        {status && <div className="text-sm opacity-70 mt-2">{status}</div>}
        {rows.length > 0 && (
          <div className="mt-4">
            <Table headers={["Name", "Email", "Role"]} rows={rows.map((r) => [r.Name, r.Email, r.Role])} />
            <div className="mt-2 text-sm opacity-70">
              Preview only — wiring to an email service can come later.
            </div>
          </div>
        )}
      </Card>

          {results.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold">Invite Results</h3>
          <div className="mt-3">
            <Table
              headers={["Name", "Email", "Status"]}
              rows={results.map((r) => [r.name, r.email, r.status])}
            />
          </div>
        </Card>
      )}
    </div>
  );
}
